import { hasSessionStorage, hasLocalStorage } from '@gig/magic-common';
import { __awaiter, __generator } from 'tslib';

// Key to store in the session storage for our last prompt time.
var LAST_PROMPT_TIME = 'magic:rc-prompt-time';
// Cross tab communication storage key for new interval
var NEW_INTERVAL = 'magic:rc-new-interval';
// Storage value to clear the timers
var STOP = 'stop';
// Interval to prompt at if none has been set by the user.
var DEFAULT_INTERVAL_MINUTES = 60;
var DEFAULT_INTERVAL_TIME = DEFAULT_INTERVAL_MINUTES * 60 * 1000;
var RealityCheckService = /** @class */ (function () {
    function RealityCheckService(isLoggedIn, lastLoginTime, userInterval) {
        if (isLoggedIn === void 0) { isLoggedIn = false; }
        if (lastLoginTime === void 0) { lastLoginTime = null; }
        if (userInterval === void 0) { userInterval = DEFAULT_INTERVAL_TIME; }
        /**
         * Current reality check listeners
         */
        this.listeners = [];
        this.sessionChangedHandlers = [];
        /**
         * Whether the reality check timers have
         * begun or are initializing.
         */
        this.isStarted = false;
        /**
         * The amount of times the reality check prompt
         * has been displayed to the user this session.
         */
        this.timesShown = 0;
        /**
         * Destructors to call when destroying.
         */
        this.destructors = [];
        /**
         * The timer identifier to cancel the next reality check
         * prompt.
         */
        this.timer = null;
        this.isLoggedIn = isLoggedIn;
        this.loginTime = lastLoginTime;
        this.userInterval = userInterval;
    }
    /**
     * Starts a timer that calls the listener whenever the
     * reality check interval has passed for the user.
     *
     * If no user is logged in then it will not call the listener
     * until the session changes.
     * @param listener The listener to call.
     * @param optional login timestamp provided by a consumer app
     */
    RealityCheckService.prototype.onRealityCheckInterval = function (listener, loginTimestamp) {
        var _this = this;
        if (!this.isStarted) {
            this.isStarted = true;
            this.startRealityCheckInterval(loginTimestamp);
        }
        this.listeners.push(listener);
        return function () {
            var index = _this.listeners.indexOf(listener);
            if (index === -1) {
                return;
            }
            _this.listeners.splice(index, 1);
            if (_this.listeners.length === 0) {
                _this.destroy();
            }
        };
    };
    /**
     * Resets the reality check instance back to its original
     * state. Removes any handlers, listeners and storage.
     * @param clearSessionData When you want to clear all associated session information
     *                         and not just the listeners.
     */
    RealityCheckService.prototype.destroy = function (clearSessionData) {
        if (clearSessionData === void 0) { clearSessionData = true; }
        this.listeners = [];
        this.isStarted = false;
        this.isLoggedIn = false;
        this.clearTimers();
        this.destructors.forEach(function (destruct) { return destruct(); });
        this.destructors = [];
        if (clearSessionData) {
            this.timesShown = 0;
            if (hasSessionStorage()) {
                sessionStorage.removeItem(LAST_PROMPT_TIME);
            }
            if (hasLocalStorage()) {
                localStorage.removeItem(NEW_INTERVAL);
            }
        }
    };
    /**
     * Stops the timers and notifies the other tabs
     */
    RealityCheckService.prototype.stopInterval = function (clearStorage) {
        if (clearStorage === void 0) { clearStorage = true; }
        this.clearTimers();
        if (clearStorage && hasLocalStorage()) {
            localStorage.setItem(NEW_INTERVAL, STOP);
        }
    };
    RealityCheckService.prototype.sessionChanged = function (isLoggedIn, lastLoginTime, interval) {
        if (this.isLoggedIn === isLoggedIn) {
            return;
        }
        this.isLoggedIn = isLoggedIn;
        if (isLoggedIn && lastLoginTime) {
            this.loginTime = lastLoginTime;
        }
        if (interval) {
            this.userInterval = interval;
        }
        this.sessionChangedHandlers.forEach(function (handler) {
            try {
                handler(isLoggedIn);
            }
            catch (e) {
                console.error('Error in Reality Check Session Changed Handler:', e);
            }
        });
    };
    RealityCheckService.prototype.onSessionChange = function (handler) {
        var _this = this;
        this.sessionChangedHandlers.push(handler);
        return function () {
            var index = _this.sessionChangedHandlers.indexOf(handler);
            if (index === -1) {
                return;
            }
            _this.sessionChangedHandlers.splice(index, 1);
        };
    };
    RealityCheckService.prototype.onLoginChange = function (handler) {
        var currentStatus = null;
        if (currentStatus !== this.isLoggedIn) {
            handler(this.isLoggedIn);
            currentStatus = this.isLoggedIn;
        }
        this.destructors.push(this.onSessionChange(function (isLoggedIn) {
            handler(isLoggedIn);
        }));
    };
    RealityCheckService.prototype.shouldEmitPrompt = function () {
        if (!hasSessionStorage()) {
            return true;
        }
        var lastTime = sessionStorage.getItem(LAST_PROMPT_TIME);
        if (!lastTime) {
            return true;
        }
        console.warn('shouldEmitPrompt', +lastTime > Date.now());
        return +lastTime > Date.now();
    };
    RealityCheckService.prototype.onRealityIntervalChange = function () {
        var _this = this;
        if (!hasLocalStorage()) {
            return;
        }
        var handler = function (event) {
            if (event.key === NEW_INTERVAL && event.newValue) {
                try {
                    if (event.newValue === STOP) {
                        _this.stopInterval(false);
                        return;
                    }
                    var interval = parseInt(event.newValue || '', 10);
                    if (_this.loginTime) {
                        _this.startTimer(interval);
                    }
                }
                catch (e) {
                    console.error('Error receiving new rg interval', e);
                }
            }
        };
        window.addEventListener('storage', handler);
        this.destructors.push(function () {
            window.removeEventListener('storage', handler);
        });
    };
    RealityCheckService.prototype.startRealityCheckInterval = function (loginTimeProvided) {
        var _this = this;
        this.isStarted = true;
        this.onRealityIntervalChange();
        this.onLoginChange(function (isLoggedIn) {
            console.warn('isLoggedIn', isLoggedIn);
            if (isLoggedIn) {
                _this.loginTime = loginTimeProvided
                    ? loginTimeProvided
                    : _this.loginTime;
                _this.startTimer(_this.userInterval);
            }
            else {
                _this.clearTimers();
                _this.loginTime = null;
                _this.timesShown = 0;
                if (hasSessionStorage()) {
                    sessionStorage.removeItem(LAST_PROMPT_TIME);
                }
            }
        });
    };
    RealityCheckService.prototype.clearTimers = function () {
        if (this.timer) {
            clearTimeout(this.timer);
        }
    };
    RealityCheckService.prototype.startTimer = function (interval) {
        var _this = this;
        this.clearTimers();
        if (!this.loginTime) {
            throw new Error('Reality Check interval started without login time being set.');
        }
        var timeout = interval - ((Date.now() - this.loginTime) % interval);
        return new Promise(function (resolve, reject) {
            _this.timer = setTimeout(function () {
                var shouldEmit = _this.shouldEmitPrompt();
                var promises = [_this.startTimer(interval)];
                if (shouldEmit) {
                    promises.push(_this.emitIntervalElapsed());
                }
                Promise.all(promises).then(function () { return resolve(); }, reject);
            }, timeout);
        });
    };
    RealityCheckService.prototype.emitIntervalElapsed = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                this.timesShown++;
                this.listeners.forEach(function (listener) {
                    try {
                        listener({
                            timesShown: _this.timesShown,
                            loginTimestamp: _this.loginTime,
                        });
                    }
                    catch (e) {
                        // tslint:disable-next-line:no-console
                        console.error('Error in Reality Check Prompt Listener:', e);
                    }
                });
                return [2 /*return*/];
            });
        });
    };
    return RealityCheckService;
}());
var REALITY_CHECK_INSTANCE;
var getRealityCheckApi = function (isLoggedIn, lastLoginTime, userInterval) {
    if (!REALITY_CHECK_INSTANCE) {
        REALITY_CHECK_INSTANCE = new RealityCheckService(isLoggedIn, lastLoginTime, userInterval);
    }
    else {
        REALITY_CHECK_INSTANCE.sessionChanged(isLoggedIn, lastLoginTime, userInterval);
    }
    return REALITY_CHECK_INSTANCE;
};

export { DEFAULT_INTERVAL_MINUTES, DEFAULT_INTERVAL_TIME, LAST_PROMPT_TIME, NEW_INTERVAL, RealityCheckService, getRealityCheckApi };
